import type {
  AppData,
  CompRef,
  DocumentServicesObject,
  DsItem,
} from '@wix/document-services-types';
import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { TPAMessage, TPAMessageCallback } from '../types/tpaMessage';
import type { TpaHelpService } from './tpaHelpService';

interface TpaSettingsPanelServiceDependencies {
  tpaHelpService: TpaHelpService;
}

export const createTpaSettingsPanelService = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  { tpaHelpService }: TpaSettingsPanelServiceDependencies,
): TpaSettingsPanelService => {
  const parseModalSizeParams = (sizeParam: number | string) => {
    if (typeof sizeParam === 'number') {
      return `${sizeParam.toString()}px`;
    }
    return sizeParam;
  };

  const getPanelParams = (
    messageData: TPAMessage['data'],
    appData: AppData,
    callback: TPAMessageCallback,
  ) => {
    const panelParams: any = structuredClone(messageData);
    panelParams.width = parseModalSizeParams(panelParams.width);
    panelParams.height = parseModalSizeParams(panelParams.height);
    panelParams.onClose = callback;

    if (appData?.isWixTPA) {
      if (panelParams.isBareMode) {
        delete panelParams.title;
      }

      panelParams.shouldNotCloseOnBlur =
        panelParams.shouldNotCloseOnBlur ||
        (panelParams?.options?.shouldNotCloseOnBlur ?? false);
    } else {
      panelParams.isBareMode = false;
      panelParams.shouldNotCloseOnBlur = false;
    }

    return panelParams;
  };

  const getModalProps = async (
    selectedComponent: CompRef,
    params: TPAMessage,
    appData: AppData,
    compData: DsItem | undefined,
    callback: TPAMessageCallback,
  ) => {
    const { deviceType, data } = params;

    const urlParams = {
      url: data?.url,
      deviceType,
      applicationId: appData?.applicationId,
      origCompId: params.originalCompId || selectedComponent?.id,
      bi: data?.bi,
    };

    const panelParams = getPanelParams(data, appData, callback);
    const modalProps = documentServicesAPI.tpa.getSettingsModalParams(
      urlParams,
      panelParams,
    );

    modalProps.widgetId = compData?.widgetId;
    modalProps.appDefinitionId = appData.appDefinitionId;

    const helpId = await tpaHelpService.getHelpId(
      'settings_modal',
      deviceType,
      selectedComponent,
      appData.appDefinitionId,
      compData?.widgetId,
    );

    modalProps.onHelp = () =>
      platformApiMethods.editor.openHelpPanel({} as AppData, '', { helpId });

    if (!params.data?.options?.leavePanelsOpen) {
      modalProps.closeOtherPanels = true;
    }
    modalProps.overrideDefaultOptions = true;

    return modalProps;
  };

  const openSettingsModal = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const [selectedComponent] =
      await platformApiMethods.editor.selection.getSelectedComponents();
    if (!msg.data?.appDefinitionId && !selectedComponent) {
      return;
    }
    let compData, appDefId: string;

    if (msg.data?.appDefinitionId) {
      appDefId = msg.data.appDefinitionId;
    } else {
      compData = documentServicesAPI.components.data.get(selectedComponent);
      appDefId = compData?.appDefinitionId;
    }

    const appData = documentServicesAPI.tpa.app.getDataByAppDefId(appDefId);

    if (!appData?.appDefinitionId) {
      return;
    }

    const modalProps = await getModalProps(
      selectedComponent,
      msg,
      appData,
      compData,
      callback,
    );

    platformApiMethods.editor.openTpaSettingsModal?.(appData, '', modalProps);
  };

  const openSettingsPanel = async (compId: string, pageId: string) => {
    await new Promise((resolve) =>
      platformApiMethods.editor.preview.togglePreview?.(
        {
          biParams: { origin: 'tpa' },
        },
        resolve,
      ),
    );

    const compToSelect = documentServicesAPI.components.get.byId(
      compId,
      pageId,
    );

    if (!compToSelect) {
      throw new Error('Component should be selected for openSettingsDialog');
    }

    platformApiMethods.editor.selection.selectComponentByCompRef(
      {} as AppData,
      '',
      { compsToSelect: [compToSelect] },
    );

    const options = {
      compRef: compToSelect,
    };

    platformApiMethods.editor.openSettingsPanel({} as AppData, options);
  };

  return { openSettingsModal, openSettingsPanel };
};

export interface TpaSettingsPanelService {
  openSettingsModal: (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => Promise<void>;
  openSettingsPanel: (compId: string, pageId: string) => Promise<void>;
}
